/**
 * Política de privacidad — página pública requerida por Clerk
 * "Require express consent to legal documents" (Privacy Policy URL).
 */
import {
  BRAND_HANDLE,
  BRAND_OG_IMAGE_URL,
  BRAND_PERSON_NAME,
  BRAND_SITE_URL,
  BRAND_SUPPORT_EMAIL,
} from "@/lib/brandConfig";
import { PRIVACY_POLICY_PATH, TERMS_OF_SERVICE_PATH } from "@/lib/legalUrls";
import { useSEO } from "@/hooks/useSEO";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Shield } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function isSpanishLang(lang: string): boolean {
  return lang.toLowerCase().startsWith("es");
}

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [canGoBack, setCanGoBack] = useState(false);
  const { i18n } = useTranslation();
  const es = isSpanishLang(i18n.language || "es");
  const host = useMemo(() => {
    try {
      return new URL(BRAND_SITE_URL).hostname;
    } catch {
      return BRAND_SITE_URL;
    }
  }, []);

  useEffect(() => {
    setCanGoBack(window.history.length > 1);
  }, []);

  const handleBack = () => {
    if (canGoBack) navigate(-1);
    else navigate("/");
  };

  const copy = es
    ? {
        title: "Política de privacidad",
        lastUpdated: "Última actualización: 4 de agosto de 2026",
        seoTitle: `Política de privacidad | ${BRAND_PERSON_NAME}`,
        seoDesc: `Cómo ${BRAND_PERSON_NAME} trata datos personales, cookies y pagos en ${host}.`,
        intro: `En ${host} (operado en relación con ${BRAND_PERSON_NAME} / @${BRAND_HANDLE}) respetamos tu privacidad. Esta política describe qué datos recolectamos, para qué los usamos y qué derechos tenés.`,
        s1t: "1. Responsable y contacto",
        s1p: `El responsable del tratamiento de los datos es el operador del sitio ${host}. Para consultas de privacidad, acceso, rectificación o baja: ${BRAND_SUPPORT_EMAIL}. También podés usar el formulario de baja en /dar-de-baja.`,
        s2t: "2. Datos que recolectamos",
        s2items: [
          "Datos de cuenta (email, nombre) cuando te registrás con Clerk.",
          "Datos de perfil de membresía (p. ej. fecha de nacimiento para verificación de mayoría de edad) que nos envíes al completar el registro.",
          "Datos de pago procesados por Stripe y/o Mercado Pago (no almacenamos el número completo de tarjeta en nuestros servidores).",
          "Datos técnicos: IP, país/provincia aproximados, navegador, páginas visitadas, eventos de embudo y, si aplica, señales de red (p. ej. VPN) solo con fines de analytics y prevención de abuso.",
          "Cookies y almacenamiento local para preferencias, sesión y medición (ver banner de cookies).",
        ],
        s3t: "3. Finalidades",
        s3items: [
          "Prestar el servicio (acceso a contenido, membresía, invitaciones y soporte).",
          "Procesar pagos y emitir comprobantes / notificaciones de compra.",
          "Prevenir fraude, abuso y accesos no autorizados.",
          "Mejorar el sitio con analytics (p. ej. PostHog, Google Analytics) y comunicaciones operativas.",
          "Cumplir obligaciones legales y responder a autoridades competentes.",
        ],
        s4t: "4. Base legal",
        s4p: "Tratamos datos con base en la ejecución del contrato (prestación del servicio que solicitaste), el consentimiento (p. ej. cookies no esenciales y checkbox de términos/privacidad en el alta), y el interés legítimo en seguridad y mejora del producto, en el marco de la Ley N.° 25.326 de Protección de Datos Personales (Argentina) y normas aplicables.",
        s5t: "5. Encargados y terceros",
        s5p: "Compartimos datos solo con proveedores necesarios para operar el servicio, por ejemplo: Clerk (autenticación), Stripe / Mercado Pago (pagos), hosting (p. ej. Railway), Cloudflare (edge/CDN), Mux (video), Resend u otros para email, y herramientas de analytics. Cada proveedor trata datos según su propio rol y políticas.",
        s6t: "6. Conservación",
        s6p: "Conservamos los datos mientras tu cuenta o la relación comercial esté activa y el tiempo adicional necesario para obligaciones legales, contables, disputas de pago o seguridad. Podés solicitar la baja o eliminación cuando corresponda.",
        s7t: "7. Tus derechos",
        s7p: "Podés solicitar acceso, rectificación, actualización o supresión de tus datos personales, y oponerte a ciertos tratamientos, escribiendo a",
        s8t: "8. Menores",
        s8p: "El servicio está dirigido a mayores de edad. No recopilamos intencionalmente datos de menores. Si detectamos una cuenta de un menor, la daremos de baja.",
        s9t: "9. Transferencias internacionales",
        s9p: "Algunos proveedores pueden procesar datos fuera de Argentina. Al usar el sitio aceptás que esos tratamientos son necesarios para prestar el servicio con las salvaguardas habituales de cada proveedor.",
        s10t: "10. Cambios",
        s10p: "Podemos actualizar esta política. La versión vigente estará siempre en esta URL. El uso continuado del sitio tras cambios relevantes implica conocimiento de la versión publicada.",
        s11t: "11. Términos y cookies",
        s11p: "El uso del sitio también se rige por los",
        termsLink: "Términos y condiciones",
        s11and: ". El banner de cookies describe preferencias de seguimiento no esencial.",
        back: "Volver",
      }
    : {
        title: "Privacy policy",
        lastUpdated: "Last updated: August 4, 2026",
        seoTitle: `Privacy policy | ${BRAND_PERSON_NAME}`,
        seoDesc: `How ${BRAND_PERSON_NAME} handles personal data, cookies, and payments on ${host}.`,
        intro: `At ${host} (operated in connection with ${BRAND_PERSON_NAME} / @${BRAND_HANDLE}) we respect your privacy. This policy explains what data we collect, how we use it, and your rights.`,
        s1t: "1. Controller and contact",
        s1p: `The data controller is the operator of ${host}. For privacy requests (access, correction, deletion): ${BRAND_SUPPORT_EMAIL}. You may also use the cancellation form at /dar-de-baja.`,
        s2t: "2. Data we collect",
        s2items: [
          "Account data (email, name) when you sign up with Clerk.",
          "Membership profile data you submit after registration (e.g. birth date for age verification).",
          "Payment data processed by Stripe and/or Mercado Pago (we do not store full card numbers on our servers).",
          "Technical data: IP, approximate country/region, browser, pages viewed, funnel events, and when applicable network signals (e.g. VPN) for analytics and abuse prevention only.",
          "Cookies and local storage for preferences, session, and measurement (see cookie banner).",
        ],
        s3t: "3. Purposes",
        s3items: [
          "Provide the service (content access, membership, invitations, support).",
          "Process payments and related purchase notifications.",
          "Prevent fraud, abuse, and unauthorized access.",
          "Improve the product with analytics (e.g. PostHog, Google Analytics) and operational communications.",
          "Comply with legal obligations and respond to competent authorities.",
        ],
        s4t: "4. Legal bases",
        s4p: "We process data to perform the contract (the service you requested), based on consent (e.g. non-essential cookies and the terms/privacy checkbox at sign-up), and legitimate interests in security and product improvement, under Argentina’s Personal Data Protection Law No. 25.326 and other applicable rules.",
        s5t: "5. Processors and third parties",
        s5p: "We share data only with providers needed to run the service, for example: Clerk (auth), Stripe / Mercado Pago (payments), hosting (e.g. Railway), Cloudflare (edge/CDN), Mux (video), email providers, and analytics tools. Each processes data under their role and policies.",
        s6t: "6. Retention",
        s6p: "We keep data while your account or commercial relationship is active and for additional periods required for legal, accounting, payment disputes, or security. You may request deletion where applicable.",
        s7t: "7. Your rights",
        s7p: "You may request access, rectification, update, or deletion of your personal data, and object to certain processing, by emailing",
        s8t: "8. Minors",
        s8p: "The service is for adults only. We do not knowingly collect data from minors. If we find a minor’s account, we will close it.",
        s9t: "9. International transfers",
        s9p: "Some providers may process data outside Argentina. By using the site you acknowledge those transfers are necessary to provide the service under each provider’s safeguards.",
        s10t: "10. Changes",
        s10p: "We may update this policy. The current version will always be available at this URL. Continued use after material changes means you are aware of the published version.",
        s11t: "11. Terms and cookies",
        s11p: "Use of the site is also governed by the",
        termsLink: "Terms of service",
        s11and: ". The cookie banner covers non-essential tracking preferences.",
        back: "Back",
      };

  useSEO({
    title: copy.seoTitle,
    description: copy.seoDesc,
    keywords: es
      ? "política de privacidad, datos personales, cookies"
      : "privacy policy, personal data, cookies",
    ogImage: BRAND_OG_IMAGE_URL,
    ogTitle: copy.seoTitle,
    ogDescription: copy.seoDesc,
    canonical: `${BRAND_SITE_URL}${PRIVACY_POLICY_PATH}`,
    author: BRAND_PERSON_NAME,
    publishedTime: "2026-08-04T00:00:00.000Z",
    modifiedTime: "2026-08-04T00:00:00.000Z",
    type: "article",
    locale: es ? "es_AR" : "en_US",
    robots: "index, follow",
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <main className="max-w-4xl mx-auto px-6 py-16 md:py-20">
        <header className="text-center mb-16">
          <div className="flex justify-between items-center mb-4">
            <button
              type="button"
              onClick={handleBack}
              className="text-sm text-white/60 hover:text-white transition-colors"
            >
              ← {copy.back}
            </button>
            <LanguageSwitcher variant="dark" />
          </div>

          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/5 border border-white/10 mb-6">
            <Shield className="w-8 h-8 text-white/80" strokeWidth={1.5} />
          </div>

          <h1 className="text-4xl md:text-5xl font-display font-medium text-white tracking-wide mb-4">
            {copy.title}
          </h1>

          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-12 h-px bg-white/20" />
            <p className="text-xs uppercase tracking-[0.25em] text-white/60 font-medium">
              {host}
            </p>
            <span className="w-12 h-px bg-white/20" />
          </div>

          <p className="text-sm text-white/50 italic">{copy.lastUpdated}</p>
        </header>

        <div className="prose prose-invert prose-lg max-w-none space-y-12 text-white/80 leading-relaxed">
          <p>{copy.intro}</p>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4 pb-3 border-b border-white/10">
              {copy.s1t}
            </h2>
            <p>{copy.s1p}</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4 pb-3 border-b border-white/10">
              {copy.s2t}
            </h2>
            <ul className="list-disc pl-5 space-y-2">
              {copy.s2items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4 pb-3 border-b border-white/10">
              {copy.s3t}
            </h2>
            <ul className="list-disc pl-5 space-y-2">
              {copy.s3items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4 pb-3 border-b border-white/10">
              {copy.s4t}
            </h2>
            <p>{copy.s4p}</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4 pb-3 border-b border-white/10">
              {copy.s5t}
            </h2>
            <p>{copy.s5p}</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4 pb-3 border-b border-white/10">
              {copy.s6t}
            </h2>
            <p>{copy.s6p}</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4 pb-3 border-b border-white/10">
              {copy.s7t}
            </h2>
            <p>
              {copy.s7p}{" "}
              <a
                href={`mailto:${BRAND_SUPPORT_EMAIL}`}
                className="text-amber-300 underline underline-offset-2 hover:text-amber-200"
              >
                {BRAND_SUPPORT_EMAIL}
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4 pb-3 border-b border-white/10">
              {copy.s8t}
            </h2>
            <p>{copy.s8p}</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4 pb-3 border-b border-white/10">
              {copy.s9t}
            </h2>
            <p>{copy.s9p}</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4 pb-3 border-b border-white/10">
              {copy.s10t}
            </h2>
            <p>{copy.s10p}</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4 pb-3 border-b border-white/10">
              {copy.s11t}
            </h2>
            <p>
              {copy.s11p}{" "}
              <a
                href={TERMS_OF_SERVICE_PATH}
                className="text-amber-300 underline underline-offset-2 hover:text-amber-200"
              >
                {copy.termsLink}
              </a>
              {copy.s11and}
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
